import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { createKnife, updateKnife, getKnifeVariantOptions } from "../api/knives";

function KnifeModal({ data, onClose, onSaved }) {
  const [platesCount, setPlatesCount] = useState("");
  const [diameterCm, setDiameterCm] = useState("");
  const [moldType, setMoldType] = useState("");
  const [mtPerPlate, setMtPerPlate] = useState("");
  const [plateAreaM2, setPlateAreaM2] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [diameterOptions, setDiameterOptions] = useState([]);
  const [moldTypeOptions, setMoldTypeOptions] = useState([]);

  useEffect(() => {
    getKnifeVariantOptions()
      .then((opts) => {
        setDiameterOptions(opts?.diameter_options || []);
        setMoldTypeOptions(opts?.mold_type_options || []);
      })
      .catch((err) => console.error("VARIANT OPTIONS LOAD ERROR", err));
  }, []);

  useEffect(() => {
    if (data) {
      setPlatesCount(String(data.plates_count ?? ""));
      setDiameterCm(String(data.diameter_cm ?? ""));
      setMoldType(String(data.mold_type ?? ""));
      setMtPerPlate(String(data.mt_per_plate ?? ""));
      setPlateAreaM2(String(data.plate_area_m2 ?? ""));
      setNotes(String(data.notes ?? ""));
    } else {
      setPlatesCount("");
      setDiameterCm("");
      setMoldType("");
      setMtPerPlate("");
      setPlateAreaM2("");
      setNotes("");
    }
  }, [data]);

  async function save() {
    setError("");

    const pc = Number(platesCount);
    const dc = Number(diameterCm);
    const mt = moldType.trim();
    const mpp = Number(mtPerPlate);
    const pam = Number(plateAreaM2);

    if (!Number.isFinite(pc) || pc <= 0) {
      setError("Tabak sayısı pozitif bir tam sayı olmalıdır.");
      return;
    }
    if (!Number.isFinite(dc) || dc <= 0) {
      setError("Tabak ölçüsü seçilmelidir.");
      return;
    }
    if (!mt) {
      setError("Kalıp tipi seçilmelidir.");
      return;
    }
    if (!Number.isFinite(mpp) || mpp <= 0) {
      setError("1 tabak için metre pozitif bir sayı olmalıdır.");
      return;
    }
    if (!Number.isFinite(pam) || pam <= 0) {
      setError("Tabak alanı pozitif bir sayı olmalıdır.");
      return;
    }

    const payload = {
      plates_count: pc,
      diameter_cm: dc,
      mold_type: mt,
      mt_per_plate: mpp,
      plate_area_m2: pam,
      notes: notes.trim() || null,
    };

    const req = data?.id ? updateKnife(data.id, payload) : createKnife(payload);
    setSaving(true);
    try {
      await req;
      await onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("KNIFE SAVE ERROR", err);
      const status = err?.response?.status;
      const dataObj = err?.response?.data;
      const details = dataObj?.error?.details;
      const message =
        dataObj?.error?.message ||
        dataObj?.message ||
        (typeof dataObj === "string" ? dataObj : null) ||
        err?.message ||
        null;
      const detailsText =
        details && typeof details === "object"
          ? details.sqlMessage || details.key || details.db_code
          : typeof details === "string"
            ? details
            : null;
      setError(
        `Kaydedilemedi${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}${
          detailsText ? `\nDetay: ${detailsText}` : ""
        }`
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {data ? "Bıçak Düzenle" : "Yeni Bıçak"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Bıçak tanım bilgilerini düzenleyin.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error" sx={{ whiteSpace: "pre-wrap" }}>{error}</Alert> : null}

          <TextField
            label="Tabak Sayısı"
            type="number"
            value={platesCount}
            onChange={(e) => setPlatesCount(e.target.value)}
            required
            disabled={saving}
            inputProps={{ min: 1, step: 1 }}
          />

          <TextField
            label="Tabak Ölçüsü (cm)"
            select
            value={diameterCm}
            onChange={(e) => setDiameterCm(e.target.value)}
            required
            disabled={saving}
          >
            {diameterOptions.map((opt) => (
              <MenuItem key={opt.value} value={String(opt.value)}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Kalıp Tipi"
            select
            value={moldType}
            onChange={(e) => setMoldType(e.target.value)}
            required
            disabled={saving}
          >
            {moldTypeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="1 Tabak için Metre"
            type="number"
            value={mtPerPlate}
            onChange={(e) => setMtPerPlate(e.target.value)}
            required
            disabled={saving}
            inputProps={{ min: 0, step: "0.000001" }}
          />

          <TextField
            label="Tabak Alanı (m²)"
            type="number"
            value={plateAreaM2}
            onChange={(e) => setPlateAreaM2(e.target.value)}
            required
            disabled={saving}
            inputProps={{ min: 0, step: "0.000001" }}
          />

          <TextField
            label="Notlar"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={saving}
            multiline
            rows={2}
            helperText="Opsiyonel"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          İptal
        </Button>
        <Button onClick={save} variant="contained" disabled={saving}>
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default KnifeModal;
